/**
 * The prototype's primitive kit.
 *
 * Deliberately local to `prototypes/verification/`, not reused from
 * `@excellent/ui`. Two reasons, and the second is the real one:
 *
 *   1. The shared atoms are bound to the dark-first token system (see
 *      `theme.css`'s header), so a light-mode proposal built on them would be
 *      inheriting colour decisions rather than making them.
 *   2. This prototype is proposing a design system, so the primitives ARE part of
 *      the proposal. A `<Verdict>` that renders a distinct glyph per status, and a
 *      `<Hash>` that copies on click, are design arguments about what this product
 *      needs — arguments you can only make by writing the primitive.
 *
 * If the proposal is accepted, these become the spec for extending the real atoms
 * (`packages/ui/src/`), per the repo's "extend the atom, don't fork it" rule. They
 * are not a second component library for product code to import — nothing outside
 * this directory may import from here, and nothing here imports from product code.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";
import type { Consequence, Tone, VerificationStatus } from "./data";
import { STATUS_GLYPH, STATUS_LABEL, STATUS_TONE, shortHash } from "./data";

/* ══════════════════════════════════════════════════════════════════════════
   ICONS — 16px stroke set, currentColor
   ══════════════════════════════════════════════════════════════════════════ */

function Svg({ children, size = 16 }: { children: ReactNode; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

export type IconName =
  | "gauge"
  | "inbox"
  | "flag"
  | "play"
  | "plug"
  | "shield"
  | "file"
  | "book"
  | "seal"
  | "package"
  | "door"
  | "cpu"
  | "chart"
  | "server"
  | "code"
  | "search"
  | "chevron-down"
  | "chevron-right"
  | "chevron-left"
  | "external"
  | "copy"
  | "check"
  | "x"
  | "alert"
  | "info"
  | "clock"
  | "download"
  | "refresh"
  | "plus"
  | "link"
  | "eye"
  | "lock"
  | "menu"
  | "arrow-right";

export function Icon({ name, size = 16 }: { name: IconName; size?: number }) {
  switch (name) {
    case "gauge":
      return (
        <Svg size={size}>
          <path d="M12 14a2 2 0 100-4 2 2 0 000 4zM13.4 10.6L18 6M3.5 19a9 9 0 1117 0" />
        </Svg>
      );
    case "inbox":
      return (
        <Svg size={size}>
          <path d="M4 13l2-8h12l2 8v5a1 1 0 01-1 1H5a1 1 0 01-1-1v-5zM4 13h4l1 2h6l1-2h4" />
        </Svg>
      );
    case "flag":
      return (
        <Svg size={size}>
          <path d="M5 21V4M5 4h11l-1.5 4L16 12H5" />
        </Svg>
      );
    case "play":
      return (
        <Svg size={size}>
          <path d="M8 5.5v13l10-6.5-10-6.5z" />
        </Svg>
      );
    case "plug":
      return (
        <Svg size={size}>
          <path d="M9 3v6M15 3v6M6 9h12v2a6 6 0 01-6 6 6 6 0 01-6-6V9zM12 17v4" />
        </Svg>
      );
    case "shield":
      return (
        <Svg size={size}>
          <path d="M12 3l7 3v5.5c0 4.3-2.9 7.9-7 9.5-4.1-1.6-7-5.2-7-9.5V6l7-3z" />
        </Svg>
      );
    case "file":
      return (
        <Svg size={size}>
          <path d="M14 3H7a1 1 0 00-1 1v16a1 1 0 001 1h10a1 1 0 001-1V7l-4-4zM14 3v4h4M9 13h6M9 17h4" />
        </Svg>
      );
    case "book":
      return (
        <Svg size={size}>
          <path d="M4 5a2 2 0 012-2h12v18H6a2 2 0 01-2-2V5zM8 3v18" />
        </Svg>
      );
    case "seal":
      return (
        <Svg size={size}>
          <path d="M12 3l2.2 1.6 2.7-.3 1 2.5 2.3 1.4-.7 2.6.7 2.6-2.3 1.4-1 2.5-2.7-.3L12 19l-2.2-1.6-2.7.3-1-2.5L3.8 13.8l.7-2.6-.7-2.6 2.3-1.4 1-2.5 2.7.3L12 3zM9.5 11.8l1.9 1.9 3.4-3.4" />
        </Svg>
      );
    case "package":
      return (
        <Svg size={size}>
          <path d="M12 3l8 4.2v9.6L12 21l-8-4.2V7.2L12 3zM4 7.2l8 4.2 8-4.2M12 11.4V21" />
        </Svg>
      );
    case "door":
      return (
        <Svg size={size}>
          <path d="M5 21V4a1 1 0 011-1h9a1 1 0 011 1v17M5 21h14M13 12h.01" />
        </Svg>
      );
    case "cpu":
      return (
        <Svg size={size}>
          <path d="M8 8h8v8H8V8zM5 5h14v14H5V5zM10 2v3M14 2v3M10 19v3M14 19v3M2 10h3M2 14h3M19 10h3M19 14h3" />
        </Svg>
      );
    case "chart":
      return (
        <Svg size={size}>
          <path d="M4 20V4M4 20h16M8 20v-6M12.5 20V8M17 20v-9" />
        </Svg>
      );
    case "server":
      return (
        <Svg size={size}>
          <path d="M4 4h16v6H4V4zM4 14h16v6H4v-6zM7.5 7h.01M7.5 17h.01" />
        </Svg>
      );
    case "code":
      return (
        <Svg size={size}>
          <path d="M9 6l-5 6 5 6M15 6l5 6-5 6" />
        </Svg>
      );
    case "search":
      return (
        <Svg size={size}>
          <path d="M11 18a7 7 0 100-14 7 7 0 000 14zM16.2 16.2L21 21" />
        </Svg>
      );
    case "chevron-down":
      return (
        <Svg size={size}>
          <path d="M6 9l6 6 6-6" />
        </Svg>
      );
    case "chevron-right":
      return (
        <Svg size={size}>
          <path d="M9 6l6 6-6 6" />
        </Svg>
      );
    case "chevron-left":
      return (
        <Svg size={size}>
          <path d="M15 6l-6 6 6 6" />
        </Svg>
      );
    case "external":
      return (
        <Svg size={size}>
          <path d="M14 4h6v6M20 4l-8 8M18 14v5a1 1 0 01-1 1H5a1 1 0 01-1-1V7a1 1 0 011-1h5" />
        </Svg>
      );
    case "copy":
      return (
        <Svg size={size}>
          <path d="M9 9h10v10H9V9zM15 9V5H5v10h4" />
        </Svg>
      );
    case "check":
      return (
        <Svg size={size}>
          <path d="M4 12.5l5.5 5.5L20 7" />
        </Svg>
      );
    case "x":
      return (
        <Svg size={size}>
          <path d="M6 6l12 12M18 6L6 18" />
        </Svg>
      );
    case "alert":
      return (
        <Svg size={size}>
          <path d="M12 4l9 16H3l9-16zM12 10v4M12 17h.01" />
        </Svg>
      );
    case "info":
      return (
        <Svg size={size}>
          <path d="M12 21a9 9 0 100-18 9 9 0 000 18zM12 11v5M12 8h.01" />
        </Svg>
      );
    case "clock":
      return (
        <Svg size={size}>
          <path d="M12 21a9 9 0 100-18 9 9 0 000 18zM12 7.5V12l3 2" />
        </Svg>
      );
    case "download":
      return (
        <Svg size={size}>
          <path d="M12 3v12M7 11l5 5 5-5M4 20h16" />
        </Svg>
      );
    case "refresh":
      return (
        <Svg size={size}>
          <path d="M20 12a8 8 0 11-2.6-5.9M20 4v4h-4" />
        </Svg>
      );
    case "plus":
      return (
        <Svg size={size}>
          <path d="M12 5v14M5 12h14" />
        </Svg>
      );
    case "link":
      return (
        <Svg size={size}>
          <path d="M10 14a4 4 0 005.7 0l3-3a4 4 0 10-5.7-5.7l-1 1M14 10a4 4 0 00-5.7 0l-3 3a4 4 0 105.7 5.7l1-1" />
        </Svg>
      );
    case "eye":
      return (
        <Svg size={size}>
          <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12S18 18.5 12 18.5 2.5 12 2.5 12zM12 14.8a2.8 2.8 0 100-5.6 2.8 2.8 0 000 5.6z" />
        </Svg>
      );
    case "lock":
      return (
        <Svg size={size}>
          <path d="M6 11h12v9H6v-9zM8.5 11V7.5a3.5 3.5 0 017 0V11" />
        </Svg>
      );
    case "menu":
      return (
        <Svg size={size}>
          <path d="M4 7h16M4 12h16M4 17h16" />
        </Svg>
      );
    case "arrow-right":
      return (
        <Svg size={size}>
          <path d="M4 12h15M13 6l6 6-6 6" />
        </Svg>
      );
  }
}

/* ══════════════════════════════════════════════════════════════════════════
   BUTTON
   ══════════════════════════════════════════════════════════════════════════ */

export type ButtonVariant = "primary" | "outline" | "ghost" | "accept" | "reject";

export function Button({
  children,
  variant = "outline",
  size,
  icon,
  onClick,
  disabled,
  title,
  type = "button",
}: {
  children?: ReactNode;
  variant?: ButtonVariant;
  size?: "sm" | "lg";
  icon?: IconName;
  onClick?: () => void;
  disabled?: boolean;
  title?: string;
  type?: "button" | "submit";
}) {
  return (
    <button
      type={type}
      className="vp-btn"
      data-variant={variant}
      data-size={size}
      onClick={onClick}
      disabled={disabled}
      title={title}
    >
      {icon ? <Icon name={icon} size={size === "sm" ? 13 : 14} /> : null}
      {children}
    </button>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   VERDICT + BADGE
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * A verification status, rendered as tone + GLYPH + label.
 *
 * The glyph is the point. This product's whole job is to communicate four
 * outcomes, one of which (UNKNOWN) means "we could not tell" and must never be
 * mistaken for the one that means "this failed". Colour alone cannot carry that
 * for a colour-blind reader or in a printed pack, so each status also gets a
 * distinct shape (✓ ✕ ? –) and its literal name in text.
 */
export function Verdict({
  status,
  size,
}: {
  status: VerificationStatus;
  size?: "lg";
}) {
  return (
    <span className="vp-badge" data-tone={STATUS_TONE[status]} data-size={size}>
      <span className="vp-badge-glyph" aria-hidden="true">
        {STATUS_GLYPH[status]}
      </span>
      {STATUS_LABEL[status]}
    </span>
  );
}

export function Badge({
  children,
  tone = "neutral",
  glyph,
  size,
}: {
  children: ReactNode;
  tone?: Tone;
  glyph?: string;
  size?: "lg";
}) {
  return (
    <span className="vp-badge" data-tone={tone} data-size={size}>
      {glyph ? (
        <span className="vp-badge-glyph" aria-hidden="true">
          {glyph}
        </span>
      ) : null}
      {children}
    </span>
  );
}

/**
 * Severity — a separate visual axis from verdict on purpose.
 *
 * "critical" describes how much a gap MATTERS; "FAIL" describes what the engine
 * FOUND. Painting them with the same red badge makes a critical UNKNOWN look like
 * a failure and a low-severity FAIL look harmless. So severity is a dot plus a
 * word, never a filled chip.
 */
export function Severity({ level }: { level: Consequence }) {
  return (
    <span className="vp-sev" data-sev={level}>
      <span className="vp-sev-dot" aria-hidden="true" />
      {level}
    </span>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   HASH — click to copy
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * A content hash. Monospace, truncated for layout, full value in the title, and
 * it copies the WHOLE digest on click — because the only thing anyone ever does
 * with a hash is paste it somewhere else to compare it.
 */
export function Hash({ value, onCopy }: { value: string; onCopy?: (v: string) => void }) {
  // `shortHash` from `data.ts` rather than a second copy of the same four lines:
  // truncation length is a display decision that must be identical everywhere a
  // digest appears, or two screens abbreviate the same hash differently.
  const short = useMemo(() => shortHash(value), [value]);

  /**
   * `stopPropagation` is required, not defensive.
   *
   * Hashes appear inside clickable table rows and list rows. Without this, one
   * click both copied the digest and navigated away from the page you wanted to
   * paste it into — the single most annoying thing a copy affordance can do.
   */
  const copy = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      void navigator.clipboard?.writeText(value).catch(() => undefined);
      onCopy?.(value);
    },
    [value, onCopy],
  );

  return (
    <button type="button" className="vp-hash" onClick={copy} title={`${value}\n\nClick to copy`}>
      {short}
    </button>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   CARD
   ══════════════════════════════════════════════════════════════════════════ */

export function Card({
  title,
  subtitle,
  actions,
  children,
  footer,
  flush,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  flush?: boolean;
}) {
  return (
    <section className="vp-card">
      {title || actions ? (
        <div className="vp-card-head">
          <div>
            <h2 className="vp-card-title">{title}</h2>
            {subtitle ? <p className="vp-card-sub">{subtitle}</p> : null}
          </div>
          {actions ? <div className="vp-card-head-actions">{actions}</div> : null}
        </div>
      ) : null}
      <div className="vp-card-body" data-flush={flush ? "true" : undefined}>
        {children}
      </div>
      {footer ? <div className="vp-card-foot">{footer}</div> : null}
    </section>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   STAT
   ══════════════════════════════════════════════════════════════════════════ */

export function Stat({
  label,
  value,
  tone,
  foot,
  onClick,
  icon,
}: {
  label: string;
  value: ReactNode;
  tone?: "pass" | "fail" | "unknown" | "accent";
  foot?: ReactNode;
  onClick?: () => void;
  icon?: IconName;
}) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag className="vp-stat" onClick={onClick} type={onClick ? "button" : undefined}>
      <span className="vp-stat-label">
        {icon ? <Icon name={icon} size={13} /> : null}
        {label}
      </span>
      <span className="vp-stat-value" data-tone={tone}>
        {value}
      </span>
      {foot ? <span className="vp-stat-foot">{foot}</span> : null}
    </Tag>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   TABLE
   ══════════════════════════════════════════════════════════════════════════ */

export interface Column<T> {
  key: string;
  header: string;
  /** Sort key extractor. A column without one renders an inert header. */
  sortValue?: (row: T) => string | number;
  render: (row: T) => ReactNode;
  /** Marks the row's identifying column, which gets the stronger ink. */
  primary?: boolean;
}

/**
 * There is deliberately NO `align` option.
 *
 * Every column in this repo left-aligns — counts, dates, money included — so a
 * table reads as clean columns sharing one left edge. That is the house standard
 * (`check-table-consistency` enforces it, and this prototype adopted it rather
 * than carrying 23 `align-ignore` comments to right-align its numerics).
 *
 * Digits still line up within a column: numeric cells carry `.vp-mono`, which sets
 * `font-variant-numeric: tabular-nums`. That gets the comparability
 * right-alignment is usually reached for, without breaking the left edge.
 */

/**
 * A sortable table.
 *
 * Sorting is real rather than decorative: this product's tables are worklists
 * ("oldest FAIL first", "highest severity first") and a header that looks
 * sortable but is not is worse than no affordance at all. Columns opt in by
 * providing `sortValue`.
 *
 * ── On the `table-ignore` below ──
 *
 * `check-table-consistency` forbids hand-rolled `<table>` and points callers at
 * the `<Table>` atoms in `packages/ui/src`. This component IS the prototype's
 * local equivalent of those atoms — the rule exists to stop tables being
 * hand-rolled at CALL sites, and every table in the prototype renders through
 * this one component. A primitive cannot be written without the element. (The
 * annotation has to sit on the line above the element rather than here: the gate
 * only scans the 400 characters preceding a match.)
 */
export function Table<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  empty,
  initialSort,
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
  initialSort?: { key: string; dir: "asc" | "desc" };
}) {
  const [sort, setSort] = useState<{ key: string; dir: "asc" | "desc" } | null>(initialSort ?? null);

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sortValue) return rows;
    const get = col.sortValue;
    return [...rows].sort((a, b) => {
      const av = get(a);
      const bv = get(b);
      const cmp = typeof av === "number" && typeof bv === "number" ? av - bv : String(av).localeCompare(String(bv));
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [rows, sort, columns]);

  const toggle = (key: string) => {
    setSort((prev) =>
      prev?.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" },
    );
  };

  if (rows.length === 0 && empty) {
    return <div className="vp-table-empty">{empty}</div>;
  }

  return (
    <div className="vp-table-wrap">
      {/* table-ignore: this IS the prototype's base table atom. */}
      <table className="vp-table">
        <thead>
          <tr>
            {columns.map((c) => {
              const sortable = Boolean(c.sortValue);
              return (
                <th
                  key={c.key}
                  data-sortable={sortable ? "true" : undefined}
                  aria-sort={
                    sort?.key === c.key
                      ? sort.dir === "asc"
                        ? "ascending"
                        : "descending"
                      : sortable
                        ? "none"
                        : undefined
                  }
                >
                  {/* The sort control is a real <button> inside the <th>, not a
                      click handler on the cell. A `<th onClick>` is invisible to
                      the keyboard — it takes no tab stop and fires on no key —
                      so `aria-sort` was announcing a control that could not be
                      reached, which is worse than not announcing one. */}
                  {sortable ? (
                    <button
                      type="button"
                      className="vp-table-sort-btn"
                      onClick={() => toggle(c.key)}
                      aria-label={`Sort by ${c.header}`}
                    >
                      {c.header}
                      <span className="vp-table-sort" aria-hidden="true">
                        {sort?.key === c.key ? (sort.dir === "asc" ? "↑" : "↓") : "↕"}
                      </span>
                    </button>
                  ) : (
                    c.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => (
            <tr
              key={rowKey(row)}
              data-clickable={onRowClick ? "true" : undefined}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {columns.map((c, i) => (
                <td key={c.key} className={c.primary ? "vp-table-primary" : undefined}>
                  {/* The row's keyboard entry point rides on the FIRST cell, as a
                      real button. `onClick` on the <tr> is a mouse convenience
                      that takes no tab stop, so without this every table in the
                      product was navigable by pointer only. One stop per row
                      rather than per cell keeps the tab order usable. */}
                  {i === 0 && onRowClick ? (
                    <button
                      type="button"
                      className="vp-row-enter"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRowClick(row);
                      }}
                    >
                      {c.render(row)}
                    </button>
                  ) : (
                    c.render(row)
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   FIELDS
   ══════════════════════════════════════════════════════════════════════════ */

export function Fields({ children }: { children: ReactNode }) {
  return <div className="vp-fields">{children}</div>;
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="vp-field">
      <div className="vp-field-label">{label}</div>
      <div className="vp-field-value">{children}</div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   CALLOUT / EMPTY
   ══════════════════════════════════════════════════════════════════════════ */

export function Callout({
  tone = "accent",
  title,
  children,
  icon = "info",
}: {
  /** `waived` reads as "an exception / a model's opinion / a redaction" — the
   *  violet family, distinct from the four verdict tones. */
  tone?: "accent" | "fail" | "unknown" | "pass" | "neutral" | "waived";
  title?: ReactNode;
  children?: ReactNode;
  icon?: IconName;
}) {
  return (
    <div className="vp-callout" data-tone={tone}>
      <span className="vp-callout-icon">
        <Icon name={icon} size={15} />
      </span>
      <div>
        {title ? <div className="vp-callout-title">{title}</div> : null}
        {children ? <div className="vp-callout-body">{children}</div> : null}
      </div>
    </div>
  );
}

export function Empty({
  title,
  children,
  action,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="vp-empty">
      <div className="vp-empty-title">{title}</div>
      {children ? <p className="vp-empty-body">{children}</p> : null}
      {action}
    </div>
  );
}

export function MissingObjectPage({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="vp-page">
      <Card>
        <Empty title={title}>{children}</Empty>
      </Card>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   TABS / SEGMENTED
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * A filter/segment row.
 *
 * Deliberately NOT `role="tablist"`. These buttons filter a list in place; there
 * is no `tabpanel`, no `aria-controls`, and no roving arrow-key navigation — and
 * the ARIA tabs pattern requires all three. Claiming the role told screen-reader
 * users "tab 3 of 5" about controls that do not behave like tabs, which is worse
 * than plain buttons. `aria-pressed` describes what they actually are.
 */
export function Tabs<K extends string>({
  tabs,
  value,
  onChange,
}: {
  tabs: { key: K; label: string; count?: number }[];
  value: K;
  onChange: (key: K) => void;
}) {
  return (
    <div className="vp-tabs">
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          aria-pressed={t.key === value}
          className="vp-tab"
          data-active={t.key === value ? "true" : undefined}
          onClick={() => onChange(t.key)}
        >
          {t.label}
          {typeof t.count === "number" ? <span className="vp-tab-count">{t.count}</span> : null}
        </button>
      ))}
    </div>
  );
}

export function Segmented<K extends string>({
  options,
  value,
  onChange,
}: {
  options: { key: K; label: string }[];
  value: K;
  onChange: (key: K) => void;
}) {
  return (
    <div className="vp-seg" role="group">
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          className="vp-seg-item"
          data-active={o.key === value ? "true" : undefined}
          onClick={() => onChange(o.key)}
          aria-pressed={o.key === value}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   FILTER BAR
   ══════════════════════════════════════════════════════════════════════════ */

export function SearchBox({
  value,
  onChange,
  placeholder = "Search…",
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="vp-search">
      <span className="vp-search-icon">
        <Icon name="search" size={14} />
      </span>
      <input
        type="search"
        value={value}
        placeholder={placeholder}
        aria-label={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

export function Select<T extends string>({
  value,
  onChange,
  options,
  label,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
  label: string;
}) {
  return (
    <select
      className="vp-select"
      value={value}
      aria-label={label}
      onChange={(e) => onChange(e.target.value as T)}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   METER — the one chart form
   ══════════════════════════════════════════════════════════════════════════ */

export function Meter({
  segments,
}: {
  segments: { tone: "pass" | "fail" | "unknown" | "waived"; value: number; label: string }[];
}) {
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  const shown = segments.filter((s) => s.value > 0);
  return (
    <div>
      <div
        className="vp-meter"
        role="img"
        aria-label={segments.map((s) => `${s.label}: ${s.value}`).join(", ")}
      >
        {shown.map((s) => (
          <div
            key={s.tone}
            className="vp-meter-seg"
            data-tone={s.tone}
            style={{ width: `${(s.value / total) * 100}%` }}
          />
        ))}
      </div>
      <div className="vp-legend">
        {segments.map((s) => (
          <span key={s.tone} className="vp-legend-item">
            <span
              className="vp-legend-swatch"
              style={{
                background:
                  s.tone === "pass"
                    ? "var(--pass)"
                    : s.tone === "fail"
                      ? "var(--fail)"
                      : s.tone === "unknown"
                        ? "#ca9a04"
                        : "var(--waived)",
              }}
              aria-hidden="true"
            />
            {s.label} <span className="vp-strong">{s.value}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   TIMELINE
   ══════════════════════════════════════════════════════════════════════════ */

export function Timeline({ children }: { children: ReactNode }) {
  return <div className="vp-timeline">{children}</div>;
}

export function TimelineItem({
  tone = "neutral",
  glyph,
  title,
  meta,
  note,
}: {
  tone?: Tone;
  glyph?: string;
  title: ReactNode;
  meta?: ReactNode;
  note?: ReactNode;
}) {
  return (
    <div className="vp-tl-item">
      <span className="vp-tl-dot" data-tone={tone === "neutral" ? undefined : tone} aria-hidden="true">
        {glyph ?? "•"}
      </span>
      <div className="vp-tl-body">
        <div className="vp-tl-title">{title}</div>
        {meta ? <div className="vp-tl-meta">{meta}</div> : null}
        {note ? <div className="vp-tl-note">{note}</div> : null}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   DIALOG
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * A modal dialog.
 *
 * Three behaviours that a `role="dialog"` div does not get for free, and whose
 * absence makes a modal unusable without a mouse:
 *
 *   1. **Escape closes it.** The backdrop click was the only exit.
 *   2. **Focus moves in on open and back out on close.** Without this, focus
 *      stays on the button behind the modal and a screen reader never enters.
 *   3. **Tab is trapped.** Otherwise Tab walks straight out of the dialog and
 *      into the page underneath, which is still fully interactive.
 *
 * `role`/`aria-modal` sit on the DIALOG, not the scrim: the scrim is a
 * full-screen backdrop, so putting them there declared the entire viewport to be
 * the dialog.
 */
export function Dialog({
  title,
  subtitle,
  children,
  footer,
  onClose,
  size,
}: {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  onClose: () => void;
  size?: "lg";
}) {
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const focusables = () =>
      Array.from(
        panel.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      );

    focusables()[0]?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const items = focusables();
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      previouslyFocused?.focus?.();
    };
  }, [onClose]);

  return (
    <div
      className="vp-scrim"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="vp-dialog" data-size={size} role="dialog" aria-modal="true" aria-label={title} ref={panel}>
        <div className="vp-dialog-head">
          <h2 className="vp-dialog-title">{title}</h2>
          {subtitle ? <p className="vp-dialog-sub">{subtitle}</p> : null}
        </div>
        <div className="vp-dialog-body">{children}</div>
        {footer ? <div className="vp-dialog-foot">{footer}</div> : null}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   FORM
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * A field label.
 *
 * `htmlFor` is REQUIRED when the label points at a single control: a bare
 * `<label>` with neither `htmlFor` nor a wrapped control is programmatically
 * detached, so a screen reader reads the input as unlabelled while a sighted
 * user sees a perfectly good label.
 *
 * When the label heads a GROUP of controls (the decision `<Choice>` list), a
 * `<label>` is the wrong element — one cannot label several controls. Pass
 * `as="group"`, which renders a plain element the group references via
 * `aria-labelledby`. See `FieldGroup` below.
 */
export function Label({
  children,
  required,
  htmlFor,
  id,
  as = "label",
}: {
  children: ReactNode;
  required?: boolean;
  htmlFor?: string;
  id?: string;
  as?: "label" | "group";
}) {
  const body = (
    <>
      {children}
      {required ? (
        <>
          <span className="vp-req" aria-hidden="true">
            ●
          </span>
          {/* The dot alone is decorative; the word is what assistive tech reads,
              and it is visually hidden so the label stays short on screen. */}
          <span className="vp-sr-only"> (required)</span>
        </>
      ) : null}
    </>
  );

  if (as === "group") {
    return (
      <span className="vp-label" id={id}>
        {body}
      </span>
    );
  }
  return (
    <label className="vp-label" htmlFor={htmlFor} id={id}>
      {body}
    </label>
  );
}

/**
 * A labelled group of radio-style `<Choice>` buttons.
 *
 * `role="radiogroup"` + `aria-labelledby` is what makes "Decision ● required"
 * apply to the six options beneath it. The options themselves carry
 * `role="radio"` and `aria-checked` (see `Choice`).
 */
export function FieldGroup({
  label,
  required,
  id,
  children,
}: {
  label: ReactNode;
  required?: boolean;
  id: string;
  children: ReactNode;
}) {
  return (
    <div>
      <Label as="group" id={`${id}-label`} required={required}>
        {label}
      </Label>
      <div role="radiogroup" aria-labelledby={`${id}-label`} className="vp-stack" data-gap="sm">
        {children}
      </div>
    </div>
  );
}

/** A single-line text input that always carries an accessible name. */
export function TextInput({
  value,
  onChange,
  placeholder,
  id,
  ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  id?: string;
  ariaLabel?: string;
}) {
  return (
    <input
      className="vp-input"
      type="text"
      id={id}
      value={value}
      placeholder={placeholder}
      aria-label={ariaLabel}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export function TextArea({
  value,
  onChange,
  placeholder,
  id,
  ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  id?: string;
  ariaLabel?: string;
}) {
  return (
    <textarea
      className="vp-textarea"
      id={id}
      value={value}
      placeholder={placeholder}
      aria-label={ariaLabel}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export function Choice({
  selected,
  title,
  desc,
  onSelect,
}: {
  selected: boolean;
  title: ReactNode;
  desc?: ReactNode;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      className="vp-choice"
      data-selected={selected ? "true" : undefined}
      onClick={onSelect}
      // `radio` + `aria-checked`, not `aria-pressed`: these are mutually
      // exclusive options in a `<FieldGroup role="radiogroup">`, and a toggle
      // button announces the wrong semantics for a one-of-N choice.
      role="radio"
      aria-checked={selected}
    >
      <span className="vp-choice-mark" aria-hidden="true" />
      <span>
        <span className="vp-choice-title">{title}</span>
        {desc ? <span className="vp-choice-desc">{desc}</span> : null}
      </span>
    </button>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   PIPELINE
   ══════════════════════════════════════════════════════════════════════════ */

/** Glyph + word per stage state, so it survives greyscale and colour blindness. */
const STAGE_STATE: Record<"done" | "active" | "blocked", { glyph: string; word: string }> = {
  done: { glyph: "✓", word: "DONE" },
  active: { glyph: "◔", word: "IN PROGRESS" },
  blocked: { glyph: "✕", word: "BLOCKED" },
};

/**
 * The loop as a horizontal stage strip.
 *
 * State is written into the step label as a glyph AND a word. The background wash
 * alone was not enough: the accent and fail washes measure 1.12:1 and 1.09:1
 * against white, so all six stages looked identical without colour — the exact
 * failure the verdict badges avoid by pairing tone with ✓ ✕ ? –.
 */
export function Pipeline({
  stages,
}: {
  stages: { name: string; desc: string; state?: "done" | "active" | "blocked" }[];
}) {
  return (
    <div className="vp-pipeline">
      {stages.map((s, i) => {
        const state = s.state ? STAGE_STATE[s.state] : null;
        return (
          <div key={s.name} className="vp-stage" data-state={s.state}>
            <div className="vp-stage-n">
              {state ? (
                <span aria-hidden="true" style={{ marginRight: "calc(var(--spacing) * 1)" }}>
                  {state.glyph}
                </span>
              ) : null}
              STEP {i + 1}
              {state ? <> · {state.word}</> : null}
            </div>
            <div className="vp-stage-name">{s.name}</div>
            <div className="vp-stage-desc">{s.desc}</div>
          </div>
        );
      })}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   PAGE SCAFFOLD
   ══════════════════════════════════════════════════════════════════════════ */

export function PageHeader({
  eyebrow,
  title,
  lede,
  actions,
}: {
  eyebrow?: string;
  title: ReactNode;
  lede?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="vp-header">
      <div className="vp-header-text">
        {eyebrow ? <div className="vp-eyebrow">{eyebrow}</div> : null}
        <h1 className="vp-h1">{title}</h1>
        {lede ? <p className="vp-lede">{lede}</p> : null}
      </div>
      {actions ? <div className="vp-header-actions">{actions}</div> : null}
    </header>
  );
}

export function PageShell({
  eyebrow,
  title,
  lede,
  actions,
  wide,
  children,
}: {
  eyebrow?: string;
  title: ReactNode;
  lede?: ReactNode;
  actions?: ReactNode;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="vp-page" data-wide={wide ? "true" : undefined}>
      <PageHeader eyebrow={eyebrow} title={title} lede={lede} actions={actions} />
      {children}
    </div>
  );
}

export function Section({
  title,
  note,
  actions,
  children,
}: {
  title?: ReactNode;
  note?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="vp-section">
      {title || note || actions ? (
        <div className="vp-section-head">
          {title ? <h2 className="vp-h2">{title}</h2> : null}
          {note ? <span className="vp-section-note">{note}</span> : null}
          {actions}
        </div>
      ) : null}
      {children}
    </section>
  );
}

export function Grid({ cols, children }: { cols: 2 | 3 | 4; children: ReactNode }) {
  return (
    <div className="vp-grid" data-cols={String(cols)}>
      {children}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   CODE
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * A policy expression / JSON payload block.
 *
 * Highlighting is a small hand-rolled tokenizer rather than a syntax library: the
 * only languages this prototype shows are the policy DSL and JSON, the strings are
 * fixtures, and adding a highlighter dependency to a prototype would be the exact
 * "abstraction the task didn't ask for" the repo warns about.
 */
export function Code({ children }: { children: string }) {
  const html = useMemo(() => highlight(children), [children]);
  return <pre className="vp-pre" dangerouslySetInnerHTML={{ __html: html }} />;
}

const KEYWORDS = new Set([
  "all",
  "any",
  "none",
  "every",
  "exists",
  "count",
  "where",
  "and",
  "or",
  "not",
  "in",
  "hours",
  "business_hours_between",
  "true",
  "false",
  "null",
]);

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string);
}

function highlight(src: string): string {
  // Split on strings first so a keyword inside a quoted literal is not recoloured.
  const parts = src.split(/("(?:[^"\\]|\\.)*")/g);
  return parts
    .map((part, i) => {
      if (i % 2 === 1) return `<span class="vp-pre-tok-str">${escapeHtml(part)}</span>`;
      return escapeHtml(part)
        .replace(/\b(\d+(?:\.\d+)?)\b/g, '<span class="vp-pre-tok-num">$1</span>')
        .replace(/\b([a-z_][a-z0-9_]*)\b/gi, (m) =>
          KEYWORDS.has(m) ? `<span class="vp-pre-tok-key">${m}</span>` : m,
        );
    })
    .join("");
}

/* ══════════════════════════════════════════════════════════════════════════
   TOASTS
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * Toasts carry a TONE, and a refused action never gets a checkmark.
 *
 * "A rationale is required. No finding closes without one." rendered with a green
 * ✓ was actively misleading: the glyph said the thing had succeeded while the
 * words said it had been refused, and the glyph is what a user takes in first.
 * A refusal gets ✕ and a red glyph; only work that actually happened gets ✓.
 */
export function Toasts({ items }: { items: { id: number; text: string; tone?: "ok" | "refused" }[] }) {
  if (items.length === 0) return null;
  return (
    <div className="vp-toasts" role="status" aria-live="polite">
      {items.map((t) => (
        <div key={t.id} className="vp-toast" data-tone={t.tone ?? "ok"}>
          <span className="vp-toast-glyph" aria-hidden="true">
            {t.tone === "refused" ? "✕" : "✓"}
          </span>
          {t.text}
        </div>
      ))}
    </div>
  );
}
